// src/pages/FundPlanner/components/Portfolios/Positions.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import ExcelGrid from "components/ExcelGrid";
import { useProfile } from "context/ProfileContext";
import usePortfolioReportPositions from "./usePortfolioReportPositions";
import {
  buildHoldingsHierarchy,
  flattenHierarchy,
  getDisplayYield,
  getPositionRowsInHierarchyOrder,
  shouldHideDateModeYtm,
} from "./portfolioReportPositions";
import exportPortfolioPositionsPdf from "pages/FundPlanner/reports/exportPortfolioPositionsPdf";

/* ────────────────────────────────
   Constants
──────────────────────────────── */
const DEFAULT_EXPANDED_PREFIXES = ["portfolio:", "fundType:"];

/* ────────────────────────────────
   Helpers
──────────────────────────────── */
function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function trimToEmpty(value) {
  return String(value ?? "").trim();
}

function parseIsoDate(value) {
  const s = trimToEmpty(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = parseIsoDate(value);
  if (!d) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatHeadingDate(value) {
  const d = parseIsoDate(value);
  if (!d) return "—";
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatPercentFromPercentValue(value, digits = 3) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return `${n.toFixed(digits)}%`;
}

function formatPercentFromDecimal(value, digits = 3) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatDays(value) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return Math.round(n).toLocaleString();
}

function formatPrice(value, digits = 4) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return n.toFixed(digits);
}

function formatDollar(value, digits = 2) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function rowClassName(row) {
  if (row.row_type === "portfolio") {
    return "bg-[hsl(var(--muted))/0.18] font-semibold";
  }
  if (row.row_type === "fund_type") {
    return "bg-[hsl(var(--muted))/0.10] font-medium";
  }
  if (row.row_type === "fund") {
    return "bg-[hsl(var(--muted))/0.05] font-medium";
  }
  return "";
}

/* ────────────────────────────────
   Component
──────────────────────────────── */
export default function Positions({
  reportScope,
  reportData = null,
  portfolioName = "",
  onExportStateChange,
}) {
  const { selectedOrg } = useProfile();
  const {
    isLoading,
    loadError,
    rows: positionRows,
    asOfDate,
    reportMode,
    startDate,
    endDate,
  } = usePortfolioReportPositions(reportScope, {
    enabled: !reportData,
    data: reportData,
  });

  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const hierarchy = useMemo(
    () => buildHoldingsHierarchy(positionRows),
    [positionRows]
  );

  useEffect(() => {
    const next = new Set();
    for (const node of hierarchy) {
      if (DEFAULT_EXPANDED_PREFIXES.some((prefix) => node.key.startsWith(prefix))) {
        next.add(node.key);
      }
      for (const child of node.children || []) {
        if (DEFAULT_EXPANDED_PREFIXES.some((prefix) => child.key.startsWith(prefix))) {
          next.add(child.key);
        }
      }
    }
    setExpandedKeys(next);
  }, [hierarchy]);

  const visibleRows = useMemo(
    () => flattenHierarchy(hierarchy, expandedKeys),
    [hierarchy, expandedKeys]
  );

  const copyTableRows = useMemo(
    () => getPositionRowsInHierarchyOrder(hierarchy),
    [hierarchy]
  );

  const copyTableHeading =
    reportMode === "period"
      ? `Report - Period Mode: ${formatHeadingDate(startDate)} to ${formatHeadingDate(endDate)}`
      : `Report - Single Date Mode: ${formatHeadingDate(asOfDate)}`;

  const allExpandableKeys = useMemo(() => {
    const keys = [];

    const visit = (nodes) => {
      for (const node of nodes || []) {
        if (node.is_expandable) keys.push(node.key);
        visit(node.children);
      }
    };

    visit(hierarchy);
    return keys;
  }, [hierarchy]);

  const isAllExpanded =
    allExpandableKeys.length > 0 &&
    allExpandableKeys.every((key) => expandedKeys.has(key));
  const isAllCollapsed = expandedKeys.size === 0;

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const columns = useMemo(() => {
    return [
      {
        key: "display_name",
        label: "Level / Name",
        width: "min-w-[340px]",
        render: (row) => {
          const depth = Number(row.depth || 0);
          const padLeft = 4 + depth * 18;

          let text = "";
          if (row.row_type === "portfolio") {
            text = `Portfolio: ${row.label}`;
          } else if (row.row_type === "fund_type") {
            text = `Type: ${row.label}`;
          } else if (row.row_type === "fund") {
            text = `Fund: ${row.label}`;
          } else {
            text = row.is_pending_settlement
              ? `${row.display_name} (Pending Settlement)`
              : row.display_name;
          }

          return (
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: `${padLeft}px` }}
            >
              {row.is_expandable ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(row.key);
                  }}
                  className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-[hsl(var(--muted))/0.35]"
                  title={expandedKeys.has(row.key) ? "Collapse" : "Expand"}
                >
                  {expandedKeys.has(row.key) ? (
                    <ChevronDown className="h-4 w-4 text-muted" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted" />
                  )}
                </button>
              ) : (
                <span className="inline-block w-5" />
              )}

              <span className="truncate">{text}</span>
            </div>
          );
        },
        copyValue: (row) => {
          if (row.row_type === "portfolio") return `Portfolio: ${row.label}`;
          if (row.row_type === "fund_type") return `Type: ${row.label}`;
          if (row.row_type === "fund") return `Fund: ${row.label}`;
          return row.is_pending_settlement
            ? `${row.display_name} (Pending Settlement)`
            : row.display_name;
        },
      },
      {
        key: "settlement_date",
        label: "Settle Date",
        width: "min-w-[105px]",
        mono: true,
        date: true,
        render: (row) =>
          row.row_type === "position" ? row.settlement_date || "" : "",
        copyValue: (row) =>
          row.row_type === "position" ? formatDate(row.settlement_date) : "",
      },
      {
        key: "cusip_or_id",
        label: "CUSIP / ID",
        width: "min-w-[120px]",
        mono: true,
        render: (row) =>
          row.row_type === "position" ? row.cusip_or_id : "",
      },
      {
        key: "issuer_name",
        label: "Issuer",
        width: "min-w-[180px]",
        render: (row) =>
          row.row_type === "position" ? row.issuer_name : "",
      },
      {
        key: "rating_rollup",
        label: "Rating",
        width: "min-w-[90px]",
        render: (row) =>
          row.row_type === "position" ? row.rating_rollup : "",
      },
      {
        key: "par_display",
        label: "Par/Face Amt",
        width: "min-w-[120px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.current_amount ?? row.amount ?? row.par_amount, 2)
            : formatDollar(row.metrics?.totalPar, 2),
      },
      {
        key: "coupon_display",
        label: "Coupon",
        width: "min-w-[90px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatPercentFromPercentValue(row.coupon, 3)
            : "",
        excelValue: (row) => {
          const coupon = toNumberOrNull(row.coupon);
          return row.row_type === "position" && coupon != null
            ? coupon / 100
            : "";
        },
        excelNumberFormat: "0.000%",
      },
      {
        key: "maturity_date",
        label: "Maturity",
        width: "min-w-[105px]",
        mono: true,
        date: true,
        render: (row) =>
          row.row_type === "position" ? row.maturity_date || "" : "",
        copyValue: (row) =>
          row.row_type === "position" ? formatDate(row.maturity_date) : "",
      },
      {
        key: "wam_maturity_days_display",
        label: "WAM / Maturity (Days)",
        width: "min-w-[140px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDays(row.days_to_maturity ?? row.days_to_effective_maturity)
            : formatDays(row.metrics?.weightedAvgMaturityDays),
      },
      {
        key: "first_call_date",
        label: "Call Date",
        width: "min-w-[105px]",
        mono: true,
        date: true,
        render: (row) =>
          row.row_type === "position" ? row.first_call_date || "" : "",
        copyValue: (row) =>
          row.row_type === "position" ? formatDate(row.first_call_date) : "",
      },
      {
        key: "price_mid_display",
        label: "Last Price",
        width: "min-w-[100px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position" ? formatPrice(row.price_mid, 4) : "",
      },
      {
        key: "market_value_display",
        label: "Market Value",
        width: "min-w-[120px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.market_value, 2)
            : formatDollar(row.metrics?.totalMarketValue, 2),
      },
      {
        key: "purchase_price_display",
        label: "Purchase Price",
        width: "min-w-[110px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position" ? formatPrice(row.purchase_price, 4) : "",
      },
      {
        key: "original_cost_display",
        label: "Original Cost",
        width: "min-w-[120px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.original_cost, 2)
            : formatDollar(row.metrics?.totalOriginalCost, 2),
      },
      {
        key: "ugl_display",
        label: "Unrealized G/L",
        width: "min-w-[125px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.unrealized_gain_loss, 2)
            : formatDollar(row.metrics?.totalUnrealizedGainLoss, 2),
      },
      {
        key: "accrued_interest_display",
        label: "Accrued Interest",
        width: "min-w-[125px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.accrued_interest, 2)
            : formatDollar(row.metrics?.totalAccruedInterest, 2),
      },
      {
        key: "book_value_display",
        label: "Book Value",
        width: "min-w-[120px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) =>
          row.row_type === "position"
            ? formatDollar(row.book_value, 2)
            : formatDollar(row.metrics?.totalBookValue, 2),
      },
      ...(reportMode === "period"
        ? [
            {
              key: "net_income_display",
              label: "Net Income",
              width: "min-w-[120px]",
              mono: true,
              className: "text-right",
              numeric: true,
              render: (row) =>
                row.row_type === "position"
                  ? formatDollar(row.net_income, 2)
                  : formatDollar(row.metrics?.totalNetIncome, 2),
            },
          ]
        : []),
      {
        key: "ytm_display",
        label: "Yield",
        width: "min-w-[85px]",
        mono: true,
        className: "text-right",
        numeric: true,
        render: (row) => {
          if (row.row_type !== "position") return "";
          const displayYield = getDisplayYield(row, reportMode);
          const value = formatPercentFromDecimal(displayYield, 3);
          const isEffectiveYield =
            !shouldHideDateModeYtm(row, reportMode) &&
            row.yield_ytm_label === "Effective Yield" &&
            displayYield === row.yield_ytm_mid;
          if (!isEffectiveYield) return value;
          return <span title="Effective Yield">{value}</span>;
        },
        excelValue: (row) => getDisplayYield(row, reportMode) ?? "",
        excelNumberFormat: "0.000%",
      },
    ];
  }, [expandedKeys, reportMode, toggleExpanded]);

  const copyTableColumns = useMemo(
    () => [
      {
        key: "portfolio_name",
        label: "Portfolio",
      },
      {
        key: "fund_type_label",
        label: "Fund Type",
      },
      {
        key: "fund_name",
        label: "Fund",
      },
      {
        ...columns[0],
        key: "display_name",
        label: "Type",
        copyValue: (row) => row.display_name,
      },
      ...columns.slice(1),
    ],
    [columns]
  );

  const onPdfExport = useCallback(async () => {
    try {
      setIsPdfGenerating(true);

      await exportPortfolioPositionsPdf({
        orgId: selectedOrg?.id,
        rows: positionRows,
        asOfDate: reportMode === "period" ? endDate : asOfDate,
        reportMode,
        startDate,
        endDate,
        orgName: selectedOrg?.name || "",
        portfolioName,
      });
    } catch (err) {
      console.warn("FundPlanner Positions PDF export failed:", err);
      alert(`PDF export failed: ${err?.message || String(err)}`);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [asOfDate, endDate, portfolioName, positionRows, reportMode, selectedOrg?.id, selectedOrg?.name, startDate]);

  useEffect(() => {
    onExportStateChange?.({
      visible: !isLoading && !loadError && !!positionRows.length,
      disabled: isPdfGenerating,
      label: isPdfGenerating ? "Generating PDF..." : "Export PDF",
      onClick: onPdfExport,
    });
  }, [
    isLoading,
    loadError,
    isPdfGenerating,
    onExportStateChange,
    onPdfExport,
    positionRows.length,
  ]);

  useEffect(() => {
    return () => {
      onExportStateChange?.({
        visible: false,
        disabled: true,
        label: "Export PDF",
        onClick: null,
      });
    };
  }, [onExportStateChange]);

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="text-sm text-muted">Loading holdings...</div>
      ) : null}

      {!isLoading && loadError ? (
        <div className="text-sm text-red-600">{loadError}</div>
      ) : null}

      {!isLoading && !loadError && !visibleRows.length ? (
        <div className="text-sm text-muted">
          No holdings found for the current scope and date range.
        </div>
      ) : null}

      {!isLoading && !loadError && !!visibleRows.length ? (
        <ExcelGrid
          columns={columns}
          rows={visibleRows}
          copyTableColumns={copyTableColumns}
          copyTableRows={copyTableRows}
          copyTableHeading={copyTableHeading}
          copyTableAsHtml
          onChange={() => {}}
          showCopyButton={true}
          toolbarActions={
            <>
              <button
                type="button"
                className="linkCls text-xs disabled:cursor-default disabled:opacity-50 disabled:no-underline"
                disabled={isAllExpanded}
                onClick={() => setExpandedKeys(new Set(allExpandableKeys))}
              >
                Expand all
              </button>
              <button
                type="button"
                className="linkCls text-xs disabled:cursor-default disabled:opacity-50 disabled:no-underline"
                disabled={isAllCollapsed}
                onClick={() => setExpandedKeys(new Set())}
              >
                Collapse all
              </button>
            </>
          }
          rowClassName={rowClassName}
          viewportConstrained
          defaultCellFontClassName="text-app"
          monoCellFontClassName="font-mono text-app"
        />
      ) : null}
    </div>
  );
}
